import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getTimesheetWeeks,
  getTimesheetGrid,
  getTimesheetEmployees,
  saveTimesheetCell,
  changeTimesheetLine,
  decideTimesheetWeeks,
  fetchTimesheets,
  updateTimesheetEmployeeVisibility,
} from '../../services/timesheetService';
import { getActivityTypes } from '../../services/activityTypeService';
import { useCurrentUser } from '../../context/UserContext';
import { usePermissions } from '../../hooks/usePermission';
import FormModal from "../../components/forms/FormModal";
import DataTable from '../../components/forms/DataTable';
import Toast from '../../components/Toast';
import { useLocation } from 'react-router-dom';
import WeekPager from '../../components/common/WeekPager';
import ConfirmDialog from '../../components/ConfirmDialog';
import CustomDropdown from '../../components/forms/CustomDropdown';
import SegmentedControl from '../../components/common/SegmentedControl';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import BatchBar from '../../components/common/BatchBar';
import Avatar from '../../components/common/Avatar';
import { getProjectsForEmployee } from '../../services/peopleService';
import { getAssignedMilestones } from '../../services/projectService';
import { createTimeOffRequest, getTimeOffTypes, getTimeOffRequests, updateTimeOffRequest, cancelTimeOffRequest } from '../../services/timeOffService';
import ApprovalList from '../../components/timeoff/ApprovalList';
import { usePendingApprovals } from '../../context/PendingApprovalsContext';
import { buildTimeOffCreatePayload, buildTimeOffUpdatePayload } from '../../utils/timeOffForm';
import { countLeaveDays, formatDays } from '../../utils/leaveDays';
import styles from './Schedule.module.css';
import ui from './DashboardUI.module.css';
import tsStyles from './timesheets/TimesheetsUI.module.css';
import WeekGrid from './timesheets/WeekGrid';
import {
  STATUS,
  STORAGE_KEY,
  TEAM_COLUMNS,
  GROUPS,
  GROUP_ORDER,
  DEFAULT_FILTERS,
  ENTRY_COLUMNS,
  TYPE_LABEL,
  exportColumns,
  filterPeople,
  fmtHours,
  shortfall,
  sortPeople,
  sumDays,
  typeOf,
  weekLabel,
} from './timesheets/timesheetsConfig';
import { addDays, dayOf, mondayOf, toISODate } from '../../utils/weekDates';
import useMinimumLoader from '../../hooks/useMinimumLoader';
import useTableColumns from '../../hooks/useTableColumns';
import useSelection from '../../hooks/useSelection';
import useBatchRunner from '../../hooks/useBatchRunner';
import { errorMessage } from '../../utils/errorMessage';
import { toggleSort } from '../../utils/sortRows';
import ExportDialog from '../../components/common/ExportDialog';

const BLANK_LOG = {
  employeeId: "",
  workDate: "",
  projectId: "",
  projectName: "",
  projectCompanyName: "",
  milestoneId: "",
  activityTypeId: "",
  hours: "1",
  billable: true,
  description: "",
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const Timesheets = () => {
  const currentUser = useCurrentUser();
  const { hasPermission } = usePermissions();
  const { refreshPendingCount } = usePendingApprovals();

  const canViewAllTimesheets = hasPermission('view_timesheets');
  const canViewOwnTimesheets = hasPermission('view_own_timesheets');
  const canCreateTimesheets = hasPermission('create_timesheets');
  const canEditTimesheets = hasPermission('edit_timesheets');
  const canApproveTimesheets = hasPermission('approve_timesheets');
  const canViewTimeOff = hasPermission('timeoff:view:self') || hasPermission('timeoff:view:all') || hasPermission('timeoff:approve');
  const canEditOwnPendingTimeOff = hasPermission('timeoff:edit:self:pending');
  const canEditTeamPendingTimeOff = hasPermission('timeoff:edit:team:pending');
  const canEditApprovedTimeOff = hasPermission('timeoff:edit:approved');
  const canApproveTimeOff = hasPermission('timeoff:approve');
  const canViewAllTimeOff = hasPermission('timeoff:view:all');
  const hasAnyTimeOffPermission = canViewTimeOff || canEditOwnPendingTimeOff || canEditTeamPendingTimeOff || canEditApprovedTimeOff || canApproveTimeOff;
  const canTeam = canViewAllTimesheets || canApproveTimesheets;
  const canLog = canCreateTimesheets || canEditTimesheets;

  const today = toISODate(new Date());
  const [savedView, setView] = useState(() => {
    try { return localStorage.getItem('timesheetsView'); } catch { return null; }
  });
  const view = canTeam && savedView === 'team' ? 'team' : 'mine';
  useEffect(() => {
    try { localStorage.setItem('timesheetsView', view); } catch { /* remembering the view is optional */ }
  }, [view]);

  const [weekStart, setWeekStart] = useState(mondayOf(today));
  const [people, setPeople] = useState([]);
  const [weekProjects, setWeekProjects] = useState([]);
  const [personId, setPersonId] = useState(null);
  const [grid, setGrid] = useState(null);
  const [extraLines, setExtraLines] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [sendBack, setSendBack] = useState(null);
  const [sendBackNote, setSendBackNote] = useState("");
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });
  const showError = useCallback((err, fallback) => setToast({ message: errorMessage(err, fallback), type: 'error' }), []);
  const runBatch = useBatchRunner(setToast);
  const selection = useSelection();
  const columnState = useTableColumns(STORAGE_KEY, TEAM_COLUMNS);

  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [activeTab, setActiveTab] = useState("timesheet");
  const [employeeProjects, setEmployeeProjects] = useState([]);
  const [employeeMilestones, setEmployeeMilestones] = useState([]);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [visibilityEmployees, setVisibilityEmployees] = useState([]);
  const [isVisibilityLoading, setIsVisibilityLoading] = useState(false);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [showCancelTimeOffConfirm, setShowCancelTimeOffConfirm] = useState(false);
  const [timeOffToCancel, setTimeOffToCancel] = useState(null);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [pendingTimeOffRequests, setPendingTimeOffRequests] = useState([]);
  const [timesheetForm, setTimesheetForm] = useState(BLANK_LOG);
  const [companyFilter, setCompanyFilter] = useState("");

  const [timeOffForm, setTimeOffForm] = useState({
    timeOffId: null,
    timeOffTypeId: "",
    startDate: "",
    endDate: "",
    reason: "",
    employee_id: '',
    startHalfDay: false,
    endHalfDay: false,
  });

  // The time-off tab and the visibility list address people by user id.
  const employees = useMemo(
    () => people.filter((p) => p.user_id).map((p) => ({ id: p.user_id, name: p.name, job_title: p.job_title })),
    [people]
  );
  const selfId = people.find((p) => currentUser && p.user_id === currentUser.user_id)?.employee_id;
  const shownPersonId = personId ?? selfId ?? people[0]?.employee_id;
  const shownPerson = people.find((p) => p.employee_id === shownPersonId);

  const loadWeeks = useCallback(async () => {
    const data = await getTimesheetWeeks(weekStart);
    setPeople(data.people);
    setWeekProjects(data.projects || []);
  }, [weekStart]);

  const loadGrid = useCallback(async () => {
    if (!shownPersonId) return;
    setGrid(await getTimesheetGrid(shownPersonId, weekStart));
  }, [shownPersonId, weekStart]);

  const reload = useCallback(() => Promise.all([loadWeeks(), view === 'mine' ? loadGrid() : null]), [loadWeeks, loadGrid, view]);

  useEffect(() => {
    if (!canViewAllTimesheets && !canViewOwnTimesheets && !canApproveTimesheets) return;
    loadWeeks()
      .catch((err) => showError(err, 'Failed to load timesheets.'))
      .finally(() => setLoading(false));
  }, [loadWeeks, canViewAllTimesheets, canViewOwnTimesheets, canApproveTimesheets, showError]);

  useEffect(() => {
    setExtraLines([]);
    if (view !== 'mine') return;
    loadGrid().catch((err) => showError(err, 'Failed to load this week.'));
  }, [view, loadGrid, showError]);

  useEffect(() => {
    if (view !== 'mine' || !shownPersonId) return;
    getProjectsForEmployee(shownPersonId).then(setAssignedProjects).catch(() => setAssignedProjects([]));
  }, [view, shownPersonId]);

  useEffect(() => {
    getActivityTypes()
      .then((response) => setActivityTypes(response.activityTypes || []))
      .catch((err) => console.error("Failed to fetch activity types:", err));
  }, []);

  useEffect(() => {
    if (!hasAnyTimeOffPermission) return;

    const fetchTimeOffTypesData = async () => {
      try {
        const types = await getTimeOffTypes();
        setTimeOffTypes(types || []);

        if (types?.length > 0) {
          const firstTypeId = String(types[0].leave_type_id || types[0].id || '');
          setTimeOffForm((prev) => ({ ...prev, timeOffTypeId: firstTypeId }));
        }
      } catch (err) {
        console.error("Failed to fetch time off types:", err);
        showToast(err.message || "Failed to fetch time off types", "error");
      }
    };

    fetchTimeOffTypesData();
  }, [hasAnyTimeOffPermission]);

    const fetchTimesheetVisibilityEmployees = useCallback(async () => {
    try {
      setIsVisibilityLoading(true);
      const employeeData = await getTimesheetEmployees({ include_hidden: true });
      const mapped = employeeData.map((employee) => ({
        employee_id: employee.employee_id,
        name: employee.name,
        job_title: employee.job_title,
        hide_on_timesheets: !!employee.hide_on_timesheets,
      }));
      setVisibilityEmployees(mapped);
    } catch (error) {
      console.error("Error fetching timesheet visibility employees:", error);
      showToast(error.message || "Failed to fetch employee visibility", "error");
    } finally {
      setIsVisibilityLoading(false);
    }
  }, []);

  const handleTimesheetVisibilityToggle = async (employeeId, hidden) => {
    try {
      await updateTimesheetEmployeeVisibility(employeeId, hidden);
      await Promise.all([loadWeeks(), fetchTimesheetVisibilityEmployees()]);
      showToast(`Employee ${hidden ? 'hidden from' : 'shown on'} timesheets`, 'success');
    } catch (error) {
      console.error("Error updating timesheet visibility:", error);
      showToast(error.message || "Failed to update timesheet visibility", "error");
    }
  };

    useEffect(() => {
    const fetchProjectsAndMilestones = async () => {
      if (!timesheetForm.employeeId) {
        setEmployeeProjects([]);
        setEmployeeMilestones([]);
        return;
      }

      try {
        const projectData = await getProjectsForEmployee(timesheetForm.employeeId);
        setEmployeeProjects(projectData || []);

        // Fetch milestones if a project is selected
        if (timesheetForm.projectId) {
          const milestones = await getAssignedMilestones(timesheetForm.projectId, timesheetForm.employeeId);
          setEmployeeMilestones(milestones || []);
        } else {
          setEmployeeMilestones([]);
        }
      } catch (err) {
        console.error("Could not fetch employee's projects or milestones", err);
        showToast(err.message || "Failed to fetch employee projects", "error");
        setEmployeeProjects([]);
        setEmployeeMilestones([]);
      }
    };

    fetchProjectsAndMilestones();
  }, [timesheetForm.employeeId, timesheetForm.projectId]);

    // Sync companyFilter when modal opens or selected project changes externally (edit/prefill)
  useEffect(() => {
    if (!showTimesheetModal) {
      setCompanyFilter("");
      return;
    }
    if (timesheetForm.projectId) {
      setCompanyFilter(timesheetForm.projectCompanyName || "__none__");
    }
  }, [showTimesheetModal, timesheetForm.projectId, timesheetForm.projectCompanyName]);

  // Unique company options derived from employee's assigned projects
  const timesheetCompanyOptions = useMemo(() => {
    const names = new Set();
    let hasNone = false;
    (employeeProjects || []).forEach((p) => {
      if (p.company_name) names.add(p.company_name);
      else hasNone = true;
    });
    const opts = [{ value: "", label: "All companies" }];
    Array.from(names).sort().forEach((name) => opts.push({ value: name, label: name }));
    if (hasNone) opts.push({ value: "__none__", label: "No company" });
    return opts;
  }, [employeeProjects]);

  const filteredEmployeeProjects = useMemo(() => {
    if (!companyFilter) return employeeProjects || [];
    if (companyFilter === "__none__") {
      return (employeeProjects || []).filter((p) => !p.company_name);
    }
    return (employeeProjects || []).filter((p) => p.company_name === companyFilter);
  }, [employeeProjects, companyFilter]);

    const handleTimeOffSubmit = async (e) => {
    e.preventDefault();

    if (!timeOffForm.timeOffTypeId || !timeOffForm.startDate || !timeOffForm.endDate) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      if (timeOffForm.timeOffId) {
        await updateTimeOffRequest(timeOffForm.timeOffId, buildTimeOffUpdatePayload(timeOffForm));
        showToast('Time off request updated successfully', 'success');
      } else {
        await createTimeOffRequest(buildTimeOffCreatePayload(timeOffForm, currentUser.user_id));
        showToast('Time off request submitted successfully', 'success');
      }

      await reload();
      await fetchPendingTimeOffRequests();
      refreshPendingCount();
      setShowTimesheetModal(false);
      setActiveTab("timesheet");
      setTimeOffForm({
        employee_id: '',
        timeOffTypeId: timeOffTypes.length > 0 ? String(timeOffTypes[0].leave_type_id || timeOffTypes[0].id || '') : "",
        startDate: "",
        endDate: "",
        reason: "",
      });
    } catch (error) {
      console.error("Time off request failed:", error);
      showToast(error.response?.data?.error || error.message || "Failed to submit time off request", "error");
    }
  };

    const handleApproveTimeOff = async (timeOffId) => {
    try {
      await updateTimeOffRequest(timeOffId, { status: 'approved' });
      showToast('Time off request approved', 'success');
      await reload();
      await fetchPendingTimeOffRequests();
      refreshPendingCount();
      setShowNotificationsModal(false);
    } catch (error) {
      showToast('Failed to approve time off', 'error');
    }
  };

  const handleDenyTimeOff = async (timeOffId) => {
    try {
      await updateTimeOffRequest(timeOffId, { status: 'rejected' });
      showToast('Time off request rejected', 'success');
      await reload();
      await fetchPendingTimeOffRequests();
      refreshPendingCount();
      setShowNotificationsModal(false);
    } catch (error) {
      showToast('Failed to reject time off', 'error');
    }
  };

    const handleEditTimeOffFromNotification = (request) => {
    setTimeOffForm({
      employee_id: request.employee_id,
      timeOffId: request.leave_request_id,
      timeOffTypeId: request.time_off_type_id,
      startDate: dayOf(request.start_date),
      endDate: dayOf(request.end_date),
      reason: request.reason || "",
      startHalfDay: !!request.start_half_day,
      endHalfDay: !!request.end_half_day,
    });
    setShowTimesheetModal(true);
    setActiveTab("timeOff");
    setShowNotificationsModal(false);
  };

    const fetchPendingTimeOffRequests = useCallback(async () => {
    if (!canApproveTimeOff) return;

    try {
      const response = await getTimeOffRequests({ status: 'pending' });
      const sortedRequests = response.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setPendingTimeOffRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching pending time off requests:', error);
    }
  }, [canApproveTimeOff]);

  useEffect(() => {
    if (canApproveTimeOff) {
      fetchPendingTimeOffRequests();
    }
  }, [canApproveTimeOff, fetchPendingTimeOffRequests]);

  // ---- Week paging ----
  const goToWeek = (start) => {
    setWeekStart(start);
    selection.clear();
  };

  // ---- My week grid ----
  const lines = useMemo(() => {
    const logged = grid?.lines || [];
    const keys = new Set(logged.map((l) => l.key));
    const all = [...logged, ...extraLines.filter((l) => !keys.has(l.key))];
    const q = filters.search.trim().toLowerCase();
    return all.filter((l) =>
      (!filters.project || String(l.project_id) === filters.project)
      && (!q || [l.name, l.client_name].some((v) => (v || '').toLowerCase().includes(q)))
    );
  }, [grid, extraLines, filters.search, filters.project]);

  const scheduledByLine = useMemo(() => Object.fromEntries((grid?.scheduled || []).map((l) => [l.key, l.days])), [grid]);

  const addCandidates = useMemo(() => {
    const present = new Set([...(grid?.lines || []), ...extraLines].map((l) => l.key));
    const lineFor = (project) => ({
      key: `${project.project_id}:`,
      project_id: project.project_id,
      activity_type_id: null,
      name: project.project_name || project.name,
      client_name: project.company_name || project.client_name,
      color: project.project_color || project.color,
      is_billable: true,
      days: {},
    });
    const booked = (grid?.scheduled || []).filter((l) => !present.has(l.key)).map((l) => ({ group: "Booked this week", line: { ...l, is_billable: Boolean(l.project_id), days: {} } }));
    const bookedKeys = new Set(booked.map((o) => o.line.key));
    const assigned = assignedProjects
      .map(lineFor)
      .filter((l) => !present.has(l.key) && !bookedKeys.has(l.key))
      .map((line) => ({ group: "Your projects", line }));
    const internal = activityTypes
      .map((a) => ({ key: `:${a.activity_type_id}`, project_id: null, activity_type_id: a.activity_type_id, name: a.name, client_name: 'Internal', color: a.color, is_billable: false, days: {} }))
      .filter((l) => !present.has(l.key))
      .map((line) => ({ group: "Internal", line }));
    return [...booked, ...assigned, ...internal].map((o) => ({
      ...o,
      value: o.line.key,
      label: o.line.client_name && o.line.project_id ? `${o.line.name} · ${o.line.client_name}` : o.line.name,
    }));
  }, [grid, extraLines, assignedProjects, activityTypes]);

  const weekStatus = grid?.status?.status || 'not_submitted';
  const isOwnWeek = shownPersonId === selfId;
  const canEditWeek = weekStatus !== 'approved' && (isOwnWeek ? canLog : canEditTimesheets);
  const dailyCapacity = grid?.person?.daily_capacity ?? 8;

  const lineBody = (line, extra = {}) => ({
    employee_id: shownPersonId,
    project_id: line.project_id,
    activity_type_id: line.activity_type_id,
    ...extra,
  });

  const saveCell = async (line, date, hours) => {
    try {
      await saveTimesheetCell(lineBody(line, { work_date: date, hours, mode: 'set', is_billable: line.is_billable }));
      await reload();
    } catch (err) {
      showError(err, 'Failed to save hours.');
      await loadGrid();
    }
  };

  const setBillable = async (line, value) => {
    if (!(grid?.lines || []).some((l) => l.key === line.key)) {
      return setExtraLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, is_billable: value } : l)));
    }
    try {
      await changeTimesheetLine(lineBody(line, { action: 'billable', week_start: weekStart, is_billable: value }));
      await reload();
    } catch (err) {
      showError(err, 'Failed to change the line.');
    }
    return undefined;
  };

  const removeLine = (line) => {
    const drop = async () => {
      setConfirm(null);
      setExtraLines((prev) => prev.filter((l) => l.key !== line.key));
      if (!sumDays(line.days)) return;
      try {
        await changeTimesheetLine(lineBody(line, { action: 'remove', week_start: weekStart }));
        await reload();
      } catch (err) {
        showError(err, 'Failed to remove the line.');
      }
    };
    if (sumDays(line.days)) {
      setConfirm({ message: `Remove ${line.name} and its ${fmtHours(sumDays(line.days))} from this week?`, confirmText: 'Remove', onConfirm: drop });
    } else {
      drop();
    }
  };

  const addLine = (value) => {
    const picked = addCandidates.find((o) => o.value === value);
    if (picked) setExtraLines((prev) => [...prev, picked.line]);
  };

  // Copies booked hours into empty days only; typed hours are never overwritten.
  const fillFromSchedule = async () => {
    const logged = Object.fromEntries((grid?.lines || []).map((l) => [l.key, l.days]));
    const cells = (grid?.scheduled || []).flatMap((s) =>
      Object.entries(s.days)
        .filter(([date, hours]) => hours > 0 && !logged[s.key]?.[date])
        .map(([date, hours]) => ({ line: s, date, hours }))
    );
    if (!cells.length) return showToast('Nothing to fill: every booked day already has hours.');
    try {
      await Promise.all(cells.map(({ line, date, hours }) => saveTimesheetCell(lineBody(line, {
        work_date: date, hours, mode: 'set', is_billable: Boolean(line.project_id),
      }))));
      setExtraLines([]);
      await reload();
      return showToast(`Filled ${plural(cells.length, 'day', 'days')} from Schedule.`);
    } catch (err) {
      showError(err, 'Failed to fill from Schedule.');
      return loadGrid();
    }
  };

  // ---- Submit, approve, send back, remind (reversible ones offer Undo) ----
  const decide = (action, ids, describe, extra = {}) => runBatch(
    () => decideTimesheetWeeks({ action, week_start: weekStart, employee_ids: ids, ...extra }),
    reload,
    describe,
    action === 'remind' ? null : (res) => decideTimesheetWeeks({ action: 'restore', week_start: weekStart, employee_ids: res.previous.map((p) => p.employee_id), restore: res.previous }),
  );

  const submitWeek = () => decide('submit', [shownPersonId], () => 'Week submitted for approval.');
  const approve = (ids) => decide('approve', ids, (res) => `${plural(res.updated.length, 'week', 'weeks')} approved.`);
  const reopen = (ids) => decide('reopen', ids, () => 'Week reopened.');
  const remind = (ids) => decide('remind', ids, (res) => `Reminder sent to ${plural(res.reminded, 'person', 'people')}.`);
  const openSendBack = (ids) => { setSendBackNote(""); setSendBack(ids); };
  const confirmSendBack = (e) => {
    e.preventDefault();
    const ids = sendBack;
    setSendBack(null);
    decide('send_back', ids, (res) => `${plural(res.updated.length, 'week', 'weeks')} sent back.`, { note: sendBackNote.trim() || undefined });
    selection.clear();
  };

  // ---- Team table ----
  const f = filters;
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const rows = sortPeople(filterPeople(people, f), f.sort);
  const selectedRows = people.filter((p) => selection.selectedIds.has(p.id ?? p.employee_id));
  const submittedSelected = selectedRows.filter((p) => p.status === 'submitted');
  const remindable = selectedRows.filter((p) => p.user_id && ['not_submitted', 'sent_back'].includes(p.status));
  const toApprove = people.filter((p) => p.status === 'submitted').length;
  const group = GROUPS[f.groupBy] || null;
  const peopleRows = useMemo(() => rows.map((p) => ({ ...p, id: p.employee_id })), [rows]);

  const openPerson = (person) => {
    setPersonId(person.employee_id);
    setView('mine');
  };

  const [exporting, setExporting] = useState(null);
  // The line-by-line sheet for the week, fetched when the export runs.
  const entriesSheet = async () => [{ name: 'Line by line', columns: ENTRY_COLUMNS, rows: await fetchTimesheets({ start_date: weekStart, end_date: addDays(weekStart, 6) }) }];

  const statusPill = (status) => (
    <span className={`${tsStyles.pill} ${tsStyles[`pill${STATUS[status].tone}`] || ''}`}>{STATUS[status].label}</span>
  );

  const teamCells = {
    person: (p) => (
      <span className={tsStyles.personCell}>
        <Avatar name={p.name} imageUrl={p.avatar_url} size="sm" round variant={p.kind === 'ghost' ? 'contractor' : 'default'} />
        <span className={tsStyles.personText}>
          <span>{p.name}</span>
          <span className={tsStyles.personTitle}>{p.kind === 'ghost' ? 'Contractor · paid per hour logged' : p.job_title || ''}</span>
        </span>
      </span>
    ),
    status: (p) => (
      <span className={tsStyles.statusCell}>
        {statusPill(p.status)}
        {p.status === 'submitted' && canApproveTimesheets && (
          <>
            <button type="button" className={tsStyles.decide} aria-label={`Approve ${p.name}`} onClick={(e) => { e.stopPropagation(); approve([p.employee_id]); }}>✓</button>
            <button type="button" className={`${tsStyles.decide} ${tsStyles.decideNo}`} aria-label={`Send ${p.name}'s week back`} onClick={(e) => { e.stopPropagation(); openSendBack([p.employee_id]); }}>✕</button>
          </>
        )}
      </span>
    ),
    logged: (p) => (
      <span className={tsStyles.loggedBar}>
        <span>{`${p.logged_hours}/${p.capacity_hours}h`}</span>
        <span className={tsStyles.track}><i style={{ width: `${Math.min(100, p.capacity_hours ? (p.logged_hours / p.capacity_hours) * 100 : 0)}%` }} /></span>
      </span>
    ),
    scheduled: (p) => (
      <span className={tsStyles.personText}>
        <span>{fmtHours(p.scheduled_hours)}</span>
        {p.scheduled_hours > 0 && <span className={tsStyles.subtle}>{p.scheduled_hours > p.logged_hours ? `${fmtHours(p.scheduled_hours - p.logged_hours)} to go` : 'on track'}</span>}
      </span>
    ),
    billable: (p) => (
      <span className={tsStyles.personText}>
        <span>{fmtHours(p.billable_hours)}</span>
        <span className={tsStyles.subtle}>{p.logged_hours ? `${Math.round((p.billable_hours / p.logged_hours) * 100)}%` : ''}</span>
      </span>
    ),
    missing: (p) => (p.missing_hours > 0 ? <span className={tsStyles.missing}>{fmtHours(p.missing_hours)}</span> : '—'),
    type: (p) => TYPE_LABEL[typeOf(p)].replace('Contractors', 'Contractor'),
    job_title: (p) => p.job_title || '—',
    leave: (p) => (p.weekly_hours > p.capacity_hours ? fmtHours(p.weekly_hours - p.capacity_hours) : '—'),
  };

  // ---- Log time ----
  const openLogTime = () => {
    setTimesheetForm({
      ...BLANK_LOG,
      employeeId: shownPersonId || selfId || "",
      workDate: today >= weekStart && today <= addDays(weekStart, 6) ? today : weekStart,
    });
    setActiveTab("timesheet");
    setShowTimesheetModal(true);
  };

  // The Dashboard's Log time button lands here with the form open.
  const routeLocation = useLocation();
  useEffect(() => {
    if (!routeLocation.state?.openLogTime || loading || !canLog) return;
    openLogTime();
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation.state, loading, canLog]);

  const closeLogModal = () => {
    setShowTimesheetModal(false);
    setActiveTab("timesheet");
    setTimesheetForm(BLANK_LOG);
    setTimeOffForm({
      timeOffId: null,
      timeOffTypeId: timeOffTypes.length > 0 ? String(timeOffTypes[0].leave_type_id || timeOffTypes[0].id || '') : "",
      startDate: "",
      endDate: "",
      reason: "",
      employee_id: '',
    });
  };

  const handleTimesheetSubmit = async (e) => {
    e.preventDefault();
    const hours = Number(timesheetForm.hours);
    if (!timesheetForm.employeeId) return showToast('Please choose a person', 'error');
    if (!timesheetForm.projectId && !timesheetForm.activityTypeId) return showToast('Please select either a project or an activity type', 'error');
    if (!Number.isFinite(hours) || hours <= 0 || hours > 24) return showToast('Please enter hours between 0 and 24', 'error');
    try {
      await saveTimesheetCell({
        employee_id: Number(timesheetForm.employeeId),
        project_id: timesheetForm.projectId ? Number(timesheetForm.projectId) : null,
        activity_type_id: timesheetForm.activityTypeId ? Number(timesheetForm.activityTypeId) : null,
        milestone_id: timesheetForm.milestoneId ? Number(timesheetForm.milestoneId) : null,
        work_date: timesheetForm.workDate,
        hours,
        mode: 'add',
        is_billable: timesheetForm.billable,
        description: timesheetForm.description || '',
      });
      const logged = timesheetForm;
      closeLogModal();
      if (mondayOf(logged.workDate) !== weekStart) goToWeek(mondayOf(logged.workDate));
      else await reload();
      setPersonId(Number(logged.employeeId));
      return showToast('Time logged.');
    } catch (err) {
      return showError(err, 'Failed to log time.');
    }
  };

  const logTimeFields = [
    people.length > 1 && canEditTimesheets && {
      label: "Person",
      type: "select",
      required: true,
      value: timesheetForm.employeeId,
      onChange: (e) =>
        setTimesheetForm((prev) => ({
          ...prev,
          employeeId: e.target.value,
          projectId: "",
          projectName: "",
          projectCompanyName: "",
          milestoneId: ""
        })),
      options: [
        { value: "", label: "Select person..." },
        ...people.map((p) => ({ value: p.employee_id, label: p.name + (p.job_title ? ` (${p.job_title})` : "") })),
      ],
    },
    {
      label: "Date",
      type: "date",
      required: true,
      value: timesheetForm.workDate,
      onChange: (e) =>
        setTimesheetForm((prev) => ({ ...prev, workDate: e.target.value })),
    },
                {
              label: "Project/Activity",
              type: "custom",
              required: true,
              render: () => {
                // If editing a timesheet/booking with a project that's no longer in the active list (e.g. completed),
                // include it in the dropdown so the user can still see and manage it
                const bookingProjectInList = timesheetForm.projectId && employeeProjects?.some(
                  p => p.project_id?.toString() === timesheetForm.projectId?.toString()
                );

                const projectList = filteredEmployeeProjects;

                return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      Company
                    </label>
                    <CustomDropdown
                      options={timesheetCompanyOptions}
                      value={companyFilter}
                      onChange={(val) => {
                        setCompanyFilter(val);
                        setTimesheetForm((prev) => ({
                          ...prev,
                          projectId: "",
                          projectName: "",
                          projectCompanyName: "",
                          milestoneId: "",
                        }));
                      }}
                      disabled={!timesheetForm.employeeId || (employeeProjects.length === 0)}
                      fullWidth
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                        Project
                      </label>
                      <CustomDropdown
                        options={[
                          { value: '', label: timesheetForm.employeeId ? (projectList.length === 0 ? 'No projects available' : 'Internal (no project)') : 'Select employee first' },
                          ...(timesheetForm.projectId && timesheetForm.projectName && !bookingProjectInList
                            ? [{ value: String(timesheetForm.projectId), label: `${timesheetForm.projectName} (${timesheetForm.projectCompanyName || 'No Client'}) — Completed` }]
                            : []),
                          ...projectList.map((project) => ({
                            value: String(project.project_id),
                            label: `${project.project_name} (${project.company_name || 'No Client'})`,
                          })),
                        ]}
                        value={timesheetForm.projectId ? String(timesheetForm.projectId) : ''}
                        onChange={(val) => {
                          const project = employeeProjects.find(
                            (p) => String(p.project_id) === val
                          );
                          setTimesheetForm((prev) => ({
                            ...prev,
                            projectId: val,
                            projectName: project?.project_name || prev.projectName,
                            projectCompanyName: project?.company_name || "",
                            milestoneId: '',
                          }));
                        }}
                        disabled={!timesheetForm.employeeId || (employeeProjects.length === 0 && !timesheetForm.projectName)}
                        fullWidth
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                        Activity Type
                      </label>
                      <CustomDropdown
                        options={[
                          { value: '', label: 'Select activity type...' },
                          ...activityTypes.map((at) => ({
                            value: String(at.activity_type_id),
                            label: at.name,
                          })),
                        ]}
                        value={timesheetForm.activityTypeId ? String(timesheetForm.activityTypeId) : ''}
                        onChange={(val) =>
                          setTimesheetForm((prev) => ({
                            ...prev,
                            activityTypeId: val,
                          }))
                        }
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              );
              },
            },
                ...(employeeMilestones.length > 0 ? [{
              label: "Milestone",
              type: "select",
              required: false,
              value: timesheetForm.milestoneId,
              onChange: (e) =>
                setTimesheetForm((prev) => ({
                  ...prev,
                  milestoneId: e.target.value,
                })),
              options: [
                { value: "", label: "None" },
                ...employeeMilestones.map((m) => ({
                  value: m.milestone_id,
                  label: `${m.milestone_name}`,
                })),
              ],
            }] : []),
    {
      label: "Hours",
      type: "number",
      required: true,
      step: "0.25",
      min: "0.25",
      max: "24",
      value: timesheetForm.hours,
      onChange: (e) => setTimesheetForm((prev) => ({ ...prev, hours: e.target.value })),
    },
    {
      label: "Billable to the client",
      type: "checkbox",
      checked: timesheetForm.billable,
      onChange: (e) => setTimesheetForm((prev) => ({ ...prev, billable: e.target.checked })),
    },
    {
      label: "Description",
      type: "textarea",
      value: timesheetForm.description,
      onChange: (e) =>
        setTimesheetForm((prev) => ({ ...prev, description: e.target.value })),
    },
  ].filter(Boolean);

  // Show access denied if no view permissions
  if (!canViewAllTimesheets && !canViewOwnTimesheets && !canApproveTimesheets) {
    return (
      <div className={styles.scheduleWrapper}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Timesheets</h2>
          </div>
        </div>
        <div className={styles.accessDenied}>
          <h3>Access Denied</h3>
          <p>You do not have permission to view timesheets.</p>
        </div>
      </div>
    );
  }

  const menuActions = [
    { label: "Export…", onClick: () => setExporting("filtered") },
    ...(hasAnyTimeOffPermission ? [{
      label: "Request time off",
      onClick: () => {
        setTimeOffForm({
          timeOffId: null,
          timeOffTypeId: timeOffTypes.length > 0 ? String(timeOffTypes[0].leave_type_id || timeOffTypes[0].id || '') : "",
          startDate: weekStart,
          endDate: weekStart,
          reason: "",
          employee_id: currentUser.user_id,
        });
        setActiveTab("timeOff");
        setShowTimesheetModal(true);
      },
    }] : []),
    ...(canApproveTimeOff && pendingTimeOffRequests.length > 0 ? [{
      label: `Pending time off (${pendingTimeOffRequests.length})`,
      onClick: () => setShowNotificationsModal(true),
    }] : []),
    ...(canViewAllTimesheets && canEditTimesheets ? [{
      label: "Employees on timesheets",
      onClick: async () => {
        setShowVisibilityModal(true);
        await fetchTimesheetVisibilityEmployees();
      },
    }] : []),
  ];

  const projectOptions = [{ value: "", label: "All Projects" }, ...(view === 'team'
    ? weekProjects.map((p) => ({ value: String(p.project_id), label: p.name }))
    : [...new Map((grid?.lines || []).filter((l) => l.project_id).map((l) => [l.project_id, { value: String(l.project_id), label: l.name }])).values()])];

  const shortDays = grid && canEditWeek ? shortfall({ weekStart, today, dailyCapacity, leave: grid.leave, lines: grid.lines }) : null;
  const loggedTotal = (grid?.lines || []).reduce((s, l) => s + sumDays(l.days), 0);
  const scheduledTotal = (grid?.scheduled || []).reduce((s, l) => s + sumDays(l.days), 0);
  const billableTotal = (grid?.lines || []).filter((l) => l.is_billable).reduce((s, l) => s + sumDays(l.days), 0);

  return (
    <div className={ui.fadeInContent}>
      {toast && (
        <Toast message={toast.message} type={toast.type} action={toast.action} onClose={() => setToast(null)} />
      )}

      {showLoading ? (
        <div className={ui.pageContainer}>
          <div className={ui.toolbarSkeleton}>
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={`timesheet-toolbar-skeleton-${idx}`} className={`${ui.skeletonBlock} ${ui.toolbarSkeletonPill}`} />
            ))}
          </div>
          <div className={ui.tableSkeleton}>
            <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={`timesheet-row-skeleton-${idx}`} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className={ui.pageHeader}>
            <div className={ui.pageHeaderLeft} style={{ alignItems: "center" }}>
              <h1 className={ui.pageTitle}>Timesheets</h1>
              <WeekPager weekStart={weekStart} label={weekLabel(weekStart)} onChange={goToWeek} onToday={() => goToWeek(mondayOf(today))} />
            </div>
            <div className={ui.pageHeaderRight}>
              {canTeam && (
                <SegmentedControl
                  ariaLabel="My week or Team"
                  options={[
                    { value: 'mine', label: 'My week' },
                    { value: 'team', label: toApprove > 0 && canApproveTimesheets ? `Team (${toApprove} to approve)` : 'Team' },
                  ]}
                  value={view}
                  onChange={(v) => { if (v === 'mine') setPersonId(null); setView(v); }}
                />
              )}
              <RowActionsMenu actions={menuActions} />
              {canLog && (
                <button type="button" className={ui.addButton} onClick={openLogTime}>
                  <span className={ui.icon}>＋</span> Log time
                </button>
              )}
            </div>
          </div>

          <div className={ui.tableToolbar}>
            <input
              type="text"
              className={ui.toolbarSearch}
              placeholder={view === 'team' ? "Search people, projects…" : "Search projects…"}
              aria-label="Search"
              value={f.search}
              onChange={(e) => setFilter({ search: e.target.value })}
            />
            {view === 'team' && (
              <>
                <CustomDropdown toolbar options={[{ value: "", label: "All People" }, { value: "permanent", label: "Permanent" }, { value: "contractor", label: "Contractors" }]} value={f.type} onChange={(v) => setFilter({ type: v })} />
                <CustomDropdown toolbar options={[{ value: "", label: "All Statuses" }, ...Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))]} value={f.status} onChange={(v) => setFilter({ status: v })} />
              </>
            )}
            <CustomDropdown toolbar options={projectOptions} value={f.project} onChange={(v) => setFilter({ project: v })} />
            {view === 'team' && (
              <CustomDropdown
                toolbar
                options={[{ value: "", label: "No Grouping" }, ...Object.entries(GROUPS).map(([value, g]) => ({ value, label: `Group by ${g.label}` }))]}
                value={f.groupBy}
                onChange={(v) => setFilter({ groupBy: v })}
              />
            )}
            <button type="button" className={ui.toolbarReset} onClick={() => setFilters(DEFAULT_FILTERS)}>Clear</button>
          </div>

          {view === 'mine' ? (
            <>
              <div className={tsStyles.personBar}>
                {canTeam && people.length > 1 ? (
                  <span className={tsStyles.personSelect}>
                    <CustomDropdown
                      fullWidth
                      options={people.map((p) => ({ value: String(p.employee_id), label: p.name }))}
                      value={String(shownPersonId || '')}
                      onChange={(v) => setPersonId(Number(v))}
                    />
                  </span>
                ) : (
                  <span className={tsStyles.personName}>{shownPerson?.name}</span>
                )}
                {statusPill(weekStatus)}
                {grid && (
                  <span className={tsStyles.weekStats}>
                    {fmtHours(loggedTotal)} logged · {fmtHours(scheduledTotal)} on Schedule · {fmtHours(billableTotal)} billable
                    {shownPerson?.missing_hours > 0 && <> · <strong>{fmtHours(shownPerson.missing_hours)} missing so far</strong></>}
                  </span>
                )}
              </div>
              <div className={tsStyles.weekActions}>
                {weekStatus === 'approved' && canApproveTimesheets && (
                  <button type="button" className={tsStyles.secondaryButton} onClick={() => reopen([shownPersonId])}>Reopen week</button>
                )}
                {weekStatus === 'submitted' && canApproveTimesheets && (
                  <>
                    <button type="button" className={tsStyles.dangerButton} onClick={() => openSendBack([shownPersonId])}>Send back</button>
                    <button type="button" className={tsStyles.primaryButton} onClick={() => approve([shownPersonId])}>Approve week</button>
                  </>
                )}
                {weekStatus === 'submitted' && !canApproveTimesheets && <span className={tsStyles.weekStats}>Waiting for approval</span>}
                {canEditWeek && (
                  <>
                    <button type="button" className={tsStyles.secondaryButton} onClick={fillFromSchedule}>Fill from Schedule</button>
                    {weekStatus !== 'submitted' && <button type="button" className={tsStyles.primaryButton} onClick={submitWeek}>Submit week</button>}
                  </>
                )}
              </div>
              {weekStatus === 'sent_back' && (
                <div className={`${tsStyles.banner} ${tsStyles.bannerNote}`}>
                  Sent back{grid?.status?.note ? `: ${grid.status.note}` : ''}. Fix the hours and submit again.
                </div>
              )}
              {shortDays && (
                <div className={tsStyles.banner}>
                  {shortDays} {shortDays.includes(' and ') || shortDays.includes(',') ? 'are' : 'is'} short by {fmtHours(shownPerson?.missing_hours)} compared with capacity. Fill from Schedule to start from what was booked.
                </div>
              )}
              {grid && (
                <WeekGrid
                  weekStart={weekStart}
                  today={today}
                  lines={lines}
                  scheduled={scheduledByLine}
                  leave={grid.leave}
                  dailyCapacity={dailyCapacity}
                  readOnly={!canEditWeek}
                  addOptions={addCandidates}
                  onCell={saveCell}
                  onBillable={setBillable}
                  onRemove={removeLine}
                  onAdd={addLine}
                />
              )}
            </>
          ) : (
            <>
              <BatchBar count={selection.selectedIds.size} onClear={selection.clear}>
                {canApproveTimesheets && submittedSelected.length > 0 && (
                  <>
                    <button type="button" onClick={() => { approve(submittedSelected.map((p) => p.employee_id)); selection.clear(); }}>Approve ({submittedSelected.length})</button>
                    <button type="button" onClick={() => openSendBack(submittedSelected.map((p) => p.employee_id))}>Send back ({submittedSelected.length})</button>
                  </>
                )}
                {canApproveTimesheets && remindable.length > 0 && (
                  <button type="button" onClick={() => { remind(remindable.map((p) => p.employee_id)); selection.clear(); }}>Remind ({remindable.length})</button>
                )}
                <button type="button" onClick={() => setExporting("selected")}>Export</button>
              </BatchBar>

              {rows.length === 0 ? (
                <p style={{ padding: "24px 20px", color: "#64748b" }}>
                  {people.length === 0 ? 'No people on timesheets yet.' : 'No people match these filters.'}
                </p>
              ) : (
                <DataTable
                  columns={columnState.columns}
                  data={peopleRows}
                  storageKey={STORAGE_KEY}
                  groupedBy={group ? group.get : null}
                  groupOrder={GROUP_ORDER[f.groupBy] || []}
                  getGroupColor={() => "#85B7EB"}
                  renderGroupExtra={(groupRows) => (
                    <span className={tsStyles.groupLogged}>{fmtHours(groupRows.reduce((s, p) => s + p.logged_hours, 0))} logged</span>
                  )}
                  collapsedGroups={collapsedGroups}
                  toggleGroupCollapse={(g) => setCollapsedGroups((prev) => ({ ...prev, [g]: !prev[g] }))}
                  sortColumn={f.sort?.key}
                  sortDirection={f.sort?.dir || "asc"}
                  onSort={(key) => setFilter({ sort: toggleSort(f.sort, key) })}
                  onRowClick={openPerson}
                  onToggleColumn={columnState.toggleColumn}
                  onMoveColumn={columnState.moveColumn}
                  selectable
                  selectedIds={selection.selectedIds}
                  getRowId={(row) => row.id}
                  onToggleRow={selection.toggleRow}
                  onToggleGroup={selection.toggleGroup}
                  onToggleAll={(checked) => selection.toggleAll(peopleRows.map((r) => r.id), checked)}
                  customCellRender={teamCells}
                />
              )}
            </>
          )}
        </>
      )}

      {exporting && (
        <ExportDialog
          title="Export timesheets"
          filename={`timesheets-${weekStart}`}
          scopes={[
            { value: "filtered", label: "Rows shown", rows },
            ...(selectedRows.length ? [{ value: "selected", label: "Ticked rows", rows: selectedRows }] : []),
            { value: "all", label: "Everyone", rows: people },
          ]}
          defaultScope={exporting}
          columns={exportColumns(columnState.columns)}
          groups={Object.entries(GROUPS).map(([value, g]) => ({ value, label: g.label, get: g.get }))}
          defaultGroup={f.groupBy}
          extraSheets={entriesSheet}
          onExported={(count, kind) => showToast(`${plural(count, 'row', 'rows')} exported${kind === 'csv' ? ' as CSV' : ''}.`)}
          onClose={() => setExporting(null)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          confirmText={confirm.confirmText}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {sendBack && (
        <FormModal
          title="Send back"
          onCancel={() => setSendBack(null)}
          onSubmit={confirmSendBack}
          fields={[{ label: "Note (optional)", type: "textarea", value: sendBackNote, onChange: (e) => setSendBackNote(e.target.value) }]}
        />
      )}

      {showTimesheetModal && (canLog || hasAnyTimeOffPermission) && (
        <FormModal
          title={activeTab === "timesheet" ? "Log time" : (timeOffForm.timeOffId ? "Edit Time Off Request" : "Request Time Off")}
          onCancel={closeLogModal}
          onSubmit={activeTab === "timesheet" ? handleTimesheetSubmit : handleTimeOffSubmit}
          tabs={hasAnyTimeOffPermission ? [
            { key: "timesheet", label: "Timesheet" },
            { key: "timeOff", label: "Time Off" }
          ] : [{ key: "timesheet", label: "Timesheet" }]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          fields={activeTab === "timesheet" ? logTimeFields : [
            {
              label: "Employee",
              type: "select",
              value: timeOffForm.employee_id,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, employee_id: e.target.value })),
              options: (canApproveTimeOff || canViewAllTimeOff)
                ? [
                    { value: "", label: "Select employee..." },
                    ...(employees || []).map((e) => ({
                      value: e.id,
                      label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                    }))
                  ]
                : [{ value: timeOffForm.employee_id, label: currentUser.name + (currentUser.job_title ? ` (${currentUser.job_title})` : "") }],
            },
            {
              label: "Time Off Type",
              type: "select",
              required: true,
              value: timeOffForm.timeOffTypeId,
              disabled: timeOffTypes.length === 0,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, timeOffTypeId: e.target.value })),
              options: (() => {
                if (timeOffTypes.length === 0) {
                  return [{ value: "", label: "Loading time off types..." }];
                }
                return timeOffTypes.map(type => ({
                  value: String(type.leave_type_id || type.id || ''),
                  label: type.name || 'Unnamed'
                }));
              })()
            },
            {
              label: "Dates",
              type: "custom",
              render: () => {
                const sameDay = timeOffForm.startDate && timeOffForm.startDate === timeOffForm.endDate;
                const days = countLeaveDays(timeOffForm.startDate, timeOffForm.endDate, timeOffForm.startHalfDay, timeOffForm.endHalfDay);
                return (
                  <div>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
                        <input
                          type="date"
                          value={timeOffForm.startDate}
                          onChange={(e) =>
                            setTimeOffForm((prev) => ({ ...prev, startDate: e.target.value }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
                        <input
                          type="date"
                          value={timeOffForm.endDate}
                          onChange={(e) =>
                            setTimeOffForm((prev) => ({ ...prev, endDate: e.target.value }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '0.85rem', color: '#374151' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="checkbox"
                          checked={!!timeOffForm.startHalfDay}
                          onChange={(e) =>
                            setTimeOffForm((prev) => ({ ...prev, startHalfDay: e.target.checked }))}
                        />
                        {sameDay ? 'Half day' : 'First day is a half-day'}
                      </label>
                      {!sameDay && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input
                            type="checkbox"
                            checked={!!timeOffForm.endHalfDay}
                            onChange={(e) =>
                              setTimeOffForm((prev) => ({ ...prev, endHalfDay: e.target.checked }))}
                          />
                          Last day is a half-day
                        </label>
                      )}
                    </div>
                    {timeOffForm.startDate && timeOffForm.endDate && (
                      <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#6b7280' }}>
                        {formatDays(days)} working day{days === 1 ? '' : 's'} (weekends excluded)
                      </div>
                    )}
                  </div>
                );
              }
            },
            {
              label: "Reason",
              type: "textarea",
              value: timeOffForm.reason,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, reason: e.target.value })),
            },
          ]}
          footerActions={
            activeTab === "timesheet"
              ? null
              : (timeOffForm.timeOffId && (
                  <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <button
                      type="button"
                      className={styles.buttonDanger}
                      onClick={() => {
                        setTimeOffToCancel(timeOffForm.timeOffId);
                        setShowCancelTimeOffConfirm(true);
                      }}
                    >
                      Cancel Request
                    </button>
                  </div>
                ))
          }
        />
      )}

      {showCancelTimeOffConfirm && (
        <ConfirmDialog
          message="Are you sure you want to cancel this time off request? This action cannot be undone."
          onConfirm={async () => {
            try {
              await cancelTimeOffRequest(timeOffToCancel);
              setShowTimesheetModal(false);
              setActiveTab("timesheet");
              setShowCancelTimeOffConfirm(false);
              setTimeOffToCancel(null);
              await reload();
              await fetchPendingTimeOffRequests();
              showToast('Time off request cancelled successfully', 'success');
            } catch (err) {
              console.error("Failed to cancel time off request", err);
              showToast(err.message || "Failed to cancel time off request", "error");
            }
          }}
          onCancel={() => {
            setShowCancelTimeOffConfirm(false);
            setTimeOffToCancel(null);
          }}
          confirmText="Cancel Request"
          cancelText="Cancel"
        />
      )}

      {showNotificationsModal && (
        <div className={styles.timeOffApprovalOverlay} onClick={() => setShowNotificationsModal(false)}>
          <div className={styles.timeOffApprovalDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.timeOffApprovalHeader}>
              <h3>Pending Time Off Requests</h3>
              <button className={styles.timeOffApprovalCloseBtn} onClick={() => setShowNotificationsModal(false)}>×</button>
            </div>
            <div className={styles.notificationsBody}>
              <ApprovalList
                requests={pendingTimeOffRequests}
                onApprove={handleApproveTimeOff}
                onDeny={handleDenyTimeOff}
                onEdit={handleEditTimeOffFromNotification}
              />
            </div>
          </div>
        </div>
      )}

      {showVisibilityModal && (
        <FormModal
          title="Employee Visibility (Timesheets)"
          onCancel={() => setShowVisibilityModal(false)}
          onSubmit={(e) => {
            e.preventDefault();
            setShowVisibilityModal(false);
          }}
          fields={[
            {
              type: "custom",
              render: () => (
                <div style={{ maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {isVisibilityLoading ? (
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading employees...</div>
                  ) : visibilityEmployees.length === 0 ? (
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>No employees found.</div>
                  ) : (
                    visibilityEmployees.map((employee) => (
                      <label
                        key={employee.employee_id}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      >
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1e293b' }}>{employee.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{employee.job_title || 'No title'}</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={!employee.hide_on_timesheets}
                          onChange={(e) => handleTimesheetVisibilityToggle(employee.employee_id, !e.target.checked)}
                        />
                      </label>
                    ))
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default Timesheets;
